import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useVideoFetch } from "@/hooks/useVideoFetch";
import VideoResults from "@/components/VideoResults";
import { ErrorDisplay } from "@/components/ErrorDisplay";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FaTiktok } from "react-icons/fa";
import { HelpCircle, X, Download, ArrowLeft, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { createOrUpdateUser, createCollection, storeVideos } from "@/services/supabase/dataService";
import { toast } from "sonner";

// Functions to handle storing collection data
const saveCollectionToHistory = async (url: string, videos: any[]) => {
  try {
    // For demo purposes, we'll use a mock user ID
    // In a real app, this would come from authentication
    const userId = await createOrUpdateUser(
      "user@example.com", 
      "Demo", 
      "User"
    );
    
    if (!userId) {
      console.error("Failed to get or create user");
      return;
    }
    
    // Calculate total duration
    const totalDuration = videos.reduce((total, video) => {
      return total + (video.duration || 0);
    }, 0);
    
    // Create collection
    const collectionId = await createCollection(
      userId,
      url,
      videos.length,
      totalDuration
    );
    
    if (!collectionId) {
      console.error("Failed to create collection");
      return;
    }
    
    // Store videos
    await storeVideos(collectionId, videos);
    
    return collectionId;
  } catch (error) {
    console.error("Error saving collection to history:", error);
    return null;
  }
};

const ScanPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialUrl = queryParams.get("url") || "";
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  
  const {
    url,
    setUrl,
    isLoading,
    videos,
    errorMessage,
    isOnline,
    handleSubmit,
    handleRetry,
    isAutoFetching
  } = useVideoFetch();

  useEffect(() => {
    if (initialUrl) {
      setUrl(initialUrl);
    }
  }, [initialUrl, setUrl]);

  // Auto-collapse the form when videos are loaded
  useEffect(() => {
    if (videos.length > 0 && !isLoading && !isAutoFetching) {
      setIsFormOpen(false);
    }
  }, [videos.length, isLoading, isAutoFetching]);
  
  // Save collection to history when videos are loaded
  useEffect(() => {
    const saveCollection = async () => {
      // Only save if we have videos and a URL and we're not already saving
      if (videos.length > 0 && url && !isLoading && !isAutoFetching && !isSavingCollection) {
        try {
          setIsSavingCollection(true);
          await saveCollectionToHistory(url, videos);
          // No need to show a toast, as this happens in the background
        } catch (error) {
          console.error("Error saving collection:", error);
        } finally {
          setIsSavingCollection(false);
        }
      }
    };
    
    saveCollection();
  }, [videos, url, isLoading, isAutoFetching]);

  return <div className="min-h-screen bg-[#090c15]">
      <Header />
      <main className="max-w-7xl mx-auto px-4">
        <div className="py-12">
          <h1 className="text-4xl font-bold text-center mb-2">
            Download TikTok <span className="text-[#00E5FF]">Collections</span>
          </h1>
          <p className="text-gray-400 text-center mb-8">
            Enter you TikTok collection link to download all videos
          </p>

          <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen} className="max-w-3xl mx-auto mb-12 transition-all duration-300">
            <CollapsibleTrigger className={cn("flex items-center justify-between w-full rounded-xl py-3 px-6 text-left font-medium", isFormOpen ? "hidden" : "bg-[#151820]/60 border border-gray-800/40 hover:bg-[#151820]/80 mb-4 transition-all duration-200")}>
              <div className="flex items-center gap-2">
                <FaTiktok className="w-4 h-4 text-[#00E5FF]" />
                <span className="text-slate-400">Scan another collection</span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </CollapsibleTrigger>
            
            <CollapsibleContent className="transition-all duration-300">
              <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
                <div className="bg-[#151820]/80 rounded-xl p-6 backdrop-blur-sm border border-gray-800/30">
                  
                  <div className="relative flex items-center gap-2">
                    <div className="relative flex-1">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <FaTiktok className="w-5 h-5 text-gray-400" />
                      </div>
                      
                      <Input type="text" placeholder="Desktop: @username/collection/name or Mobile: tiktok.com/t/code" className="h-12 pl-10 pr-24 bg-[#1C1F26] border-gray-700 text-white placeholder:text-gray-500 rounded-xl" value={url} onChange={e => setUrl(e.target.value)} disabled={isLoading || isAutoFetching} />
                      
                      {url && <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-white" onClick={() => window.open('https://support.tiktok.com', '_blank')}>
                            <HelpCircle className="w-4 h-4" />
                          </Button>
                          
                          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-gray-400 hover:text-white" onClick={() => setUrl('')}>
                            <X className="w-4 w-4" />
                          </Button>
                        </div>}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button type="submit" className={cn("h-12 rounded-xl font-medium px-6", videos.length > 0 ? "bg-transparent border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10" : "bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#151820]", (isLoading || isAutoFetching) && "opacity-50 cursor-not-allowed")} disabled={isLoading || isAutoFetching || !isOnline || !url.trim()}>
                        <Zap className="mr-2 h-4 w-4" />
                        Download Collection
                      </Button>
                      
                      {videos.length > 0 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="text-gray-400 hover:text-white" 
                          onClick={() => setIsFormOpen(false)}
                        >
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Hide
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </CollapsibleContent>
          </Collapsible>

          <ErrorDisplay errorMessage={errorMessage} isOnline={isOnline} onRetry={handleRetry} />

          {videos.length > 0 && <VideoResults videos={videos} isLoading={isLoading || isAutoFetching} isFetchingMore={false} hasMore={false} collectionUrl={url} />}
        </div>
      </main>
    </div>;
};

export default ScanPage;
